import { useState } from 'react';
import {
  Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip,
} from '@mui/material';
import { TrendingUp, TrendingDown, AccountBalance, HourglassBottom } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { EmptyState } from '../../components/EmptyState';
import { dealerPaymentsApi, type CreatePaymentInput } from '../../api/dealerPayments.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

const ENTRY_TYPES = ['invoice', 'payment', 'credit_note', 'debit_note', 'adjustment'];
const emptyEntry: CreatePaymentInput = { dealerId: 0, entryType: 'invoice', direction: 'debit', amount: 0 };

export function DealerPaymentReconciliationPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const page = 1;
  const [createOpen, setCreateOpen] = useState(false);
  const [entry, setEntry] = useState<CreatePaymentInput>(emptyEntry);
  const [error, setError] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['dealer-payments', 'reconciliation'], queryFn: () => dealerPaymentsApi.reconciliation() });
  const list = useQuery({
    queryKey: ['dealer-payments', 'list', page],
    queryFn: () => dealerPaymentsApi.list({ page, limit: 25 }),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['dealer-payments'] });
  };

  const createMut = useMutation({
    mutationFn: () => dealerPaymentsApi.create(entry),
    onSuccess: () => { setCreateOpen(false); setEntry(emptyEntry); setError(null); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const reconcileMut = useMutation({
    mutationFn: (id: number) => dealerPaymentsApi.reconcile(id, { reconciliationStatus: 'reconciled' }),
    onSuccess: invalidate,
  });

  const s = summary.data;
  const setE = <K extends keyof CreatePaymentInput>(k: K, v: CreatePaymentInput[K]) => setEntry((f) => ({ ...f, [k]: v }));

  return (
    <>
      <PageHeader
        title="Dealer Payment Reconciliation"
        subtitle="Dealer ledger — receivables, receipts and reconciliation status."
        action={
          hasPermission('dealer_payment.create') && (
            <Button variant="contained" onClick={() => { setEntry(emptyEntry); setError(null); setCreateOpen(true); }}>New Entry</Button>
          )
        }
      />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#dc2626" label="Total Debit (Receivable)" value={inr(s?.totalDebit)} icon={<TrendingUp />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#16a34a" label="Total Credit (Received)" value={inr(s?.totalCredit)} icon={<TrendingDown />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#1e40af" label="Outstanding" value={inr(s?.outstanding)} icon={<AccountBalance />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><StatCard gradient color="#d97706" label="Unreconciled" value={s?.unreconciled.count ?? 0} icon={<HourglassBottom />} hint={inr(s?.unreconciled.amount)} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Payment Ledger" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(list.data?.data.length ?? 0) === 0 ? <EmptyState title="No payment entries" dense /> : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell><TableCell>Dealer</TableCell><TableCell>Type</TableCell><TableCell>Dir</TableCell>
                      <TableCell align="right">Amount</TableCell><TableCell>UTR</TableCell><TableCell>Status</TableCell><TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(list.data?.data ?? []).map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.paymentCode}</TableCell>
                        <TableCell>#{p.dealerId}</TableCell>
                        <TableCell>{p.entryType.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{p.direction}</TableCell>
                        <TableCell align="right">{inr(p.amount)}</TableCell>
                        <TableCell>{p.utrNumber ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={p.reconciliationStatus}
                            color={p.reconciliationStatus === 'reconciled' ? 'success' : p.reconciliationStatus === 'disputed' ? 'error' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {p.reconciliationStatus === 'unreconciled' && hasPermission('dealer_payment.reconcile') && (
                            <Button size="small" onClick={() => reconcileMut.mutate(p.id)}>Reconcile</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Dealer Payment Entry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Dealer ID" type="number" value={entry.dealerId || ''} onChange={(e) => setE('dealerId', Number(e.target.value))} required fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField select label="Entry Type" value={entry.entryType} onChange={(e) => setE('entryType', e.target.value)} fullWidth>
                {ENTRY_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              <TextField select label="Direction" value={entry.direction} onChange={(e) => setE('direction', e.target.value)} fullWidth>
                <MenuItem value="debit">Debit (receivable)</MenuItem>
                <MenuItem value="credit">Credit (received)</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Amount (₹)" type="number" value={entry.amount || ''} onChange={(e) => setE('amount', Number(e.target.value))} required fullWidth />
              <TextField label="Reference No" value={entry.referenceNo ?? ''} onChange={(e) => setE('referenceNo', e.target.value)} fullWidth />
            </Stack>
            <TextField label="Narration" value={entry.narration ?? ''} onChange={(e) => setE('narration', e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!entry.dealerId || !entry.amount} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
